/**
 * Enhanced Authentication Context
 *
 * Features:
 * 1. Secure token storage (sessionStorage + encrypted localStorage)
 * 2. Proactive token refresh (5 minutes before expiration)
 * 3. Reactive token refresh (on 401 errors via axios interceptor)
 * 4. Automatic session restoration on page load
 * 5. User data management
 */

import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/apiClient';
import secureStorage from '../utils/secureStorage';
import { clearPendingRequests } from '../utils/apiClient';
import { applyUserLanguage, saveLanguageToProfile } from '../utils/userLanguage';
import { AUTH_API as AUTH_API_URL } from '../utils/apiConfig';
import i18n from '../i18n';

export const AuthContext = createContext();

// Token refresh settings
const TOKEN_LIFETIME = 3600; // 1 hour in seconds (from docs)
const REFRESH_BEFORE = 300; // Refresh 5 minutes before expiration

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Ref to store refresh timer
  const refreshTimerRef = useRef(null);

  /**
   * Parse JWT token to extract payload
   */
  const parseJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to parse JWT:', error);
      return null;
    }
  };

  // Синхронизация email/имени из JWT-токена (актуальнее устаревшего кэша, напр. после смены email)
  const syncUserFromToken = (u, payload) => {
    if (!u || !payload) return u;
    const merged = { ...u };
    if (payload.email && payload.email !== u.email) merged.email = payload.email;
    const nm = payload.user_metadata?.name || payload.user_metadata?.full_name;
    if (nm && nm !== u.name) merged.name = nm;
    if (merged.email !== u.email || merged.name !== u.name) {
      try { secureStorage.setUserData(merged); } catch (e) { /* ignore */ }
    }
    return merged;
  };

  /**
   * Schedule proactive token refresh
   */
  const scheduleTokenRefresh = useCallback((token) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!token) return;

    try {
      const payload = parseJWT(token);
      if (!payload || !payload.exp) {
        console.warn('Token missing expiration claim');
        return;
      }

      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const refreshAt = expiresAt - (REFRESH_BEFORE * 1000);
      const refreshIn = refreshAt - now;

      console.log('[Auth] Token expires at:', new Date(expiresAt).toISOString());
      console.log('[Auth] Scheduling refresh in:', Math.round(refreshIn / 1000), 'seconds');

      if (refreshIn > 0) {
        refreshTimerRef.current = setTimeout(() => {
          console.log('[Auth] Proactive token refresh triggered');
          refreshAccessToken();
        }, refreshIn);
      } else {
        console.warn('[Auth] Token expires soon, refreshing immediately');
        refreshAccessToken();
      }
    } catch (error) {
      console.error('[Auth] Failed to schedule token refresh:', error);
    }
  }, []);

  /**
   * Refresh access token using refresh token
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = secureStorage.getRefreshToken();

      if (!refreshToken) {
        console.warn('[Auth] No refresh token available');
        logout();
        return null;
      }

      console.log('[Auth] Refreshing access token...');

      const response = await api.post(`${AUTH_API_URL}/v1/auth/refresh`, {
        token: refreshToken
      });

      const { access_token, refresh_token } = response.data;

      // Update tokens
      secureStorage.setAccessToken(access_token);
      setAccessToken(access_token);

      // Refresh token may rotate (Supabase behavior)
      if (refresh_token) {
        secureStorage.setRefreshToken(refresh_token);
      }

      // Schedule next refresh
      scheduleTokenRefresh(access_token);

      console.log('[Auth] Token refreshed successfully');

      return access_token;
    } catch (error) {
      console.error('[Auth] Failed to refresh token:', error);

      // If refresh fails, logout user
      logout();
      return null;
    }
  }, [scheduleTokenRefresh]);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    try {
      console.log('[Auth] Attempting login...');

      const response = await api.post(`${AUTH_API_URL}/v1/auth/login`, {
        email,
        password
      });

      console.log('[Auth] Login response received:', {
        hasAccessToken: !!response.data.access_token,
        hasRefreshToken: !!response.data.refresh_token,
        hasUser: !!response.data.user
      });

      const { access_token, refresh_token, user: userData } = response.data;

      console.log('[Auth] User data:', userData);

      // Сбрасываем выбранный хостинг от предыдущего юзера
      try { localStorage.removeItem('waymorr_current_hosting'); } catch (e) {}

      // Store tokens and user data
      console.log('[Auth] Storing access token...');
      secureStorage.setAccessToken(access_token);

      console.log('[Auth] Storing refresh token...');
      secureStorage.setRefreshToken(refresh_token);

      console.log('[Auth] Storing user data...');
      secureStorage.setUserData(userData);

      console.log('[Auth] Updating state...');
      // Update state
      setAccessToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      // Синхронизация языка: если юзер выбрал язык на странице логина, сохраняем его в БД
      // Если нет - применяем язык из профиля
      const currentLang = i18n.language?.split('-')[0];
      if (userData?.language && userData.language !== currentLang) {
        // Юзер выбрал другой язык на логине - сохраняем его выбор в БД
        saveLanguageToProfile(userData.id, currentLang);
        userData.language = currentLang;
        secureStorage.setUserData(userData);
      } else if (userData?.language) {
        applyUserLanguage(userData.language);
      }

      console.log('[Auth] Scheduling token refresh...');
      // Schedule proactive refresh
      scheduleTokenRefresh(access_token);

      console.log('[Auth] Login successful');

      return { success: true, user: userData };
    } catch (error) {
      console.error('[Auth] Login failed:', error);
      console.error('[Auth] Error details:', {
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }, [scheduleTokenRefresh]);

  /**
   * Register user
   */
  const register = useCallback(async (userData) => {
    try {
      const response = await api.post(`${AUTH_API_URL}/v1/auth/register`, userData);

      console.log('[Auth] Registration successful');

      return { success: true };
    } catch (error) {
      console.error('[Auth] Registration failed:', error);
      throw error;
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async (skipApi = false) => {
    try {
      // Call logout API if we have a token
      if (!skipApi && accessToken) {
        await api.post(
          `${AUTH_API_URL}/v1/auth/logout`,
          {}
        ).catch(err => {
          // Ignore errors on logout API call
          console.warn('[Auth] Logout API call failed:', err);
        });
      }
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      // Clear all auth data
      secureStorage.clearAll();

      // Clear pending API requests
      clearPendingRequests();

      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      // Update state
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);

      console.log('[Auth] Logged out');

      // Redirect to login
      navigate('/login');
    }
  }, [accessToken, navigate]);

  /**
   * Restore session on mount
   */
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Migrate old tokens if needed
        secureStorage.migrate();

        const storedToken = secureStorage.getAccessToken();
        const storedUser = secureStorage.getUserData();

        if (storedToken && storedUser) {
          // Validate token expiration
          const payload = parseJWT(storedToken);

          if (payload && payload.exp) {
            const expiresAt = payload.exp * 1000;
            const now = Date.now();

            // If token expired, try to refresh
            if (expiresAt <= now) {
              console.log('[Auth] Stored token expired, refreshing...');
              const newToken = await refreshAccessToken();

              if (newToken) {
                setAccessToken(newToken);
                setUser(syncUserFromToken(storedUser, parseJWT(newToken)));
                setIsAuthenticated(true);
                if (storedUser?.language) applyUserLanguage(storedUser.language);
              }
            } else {
              // Token still valid
              setAccessToken(storedToken);
              setUser(syncUserFromToken(storedUser, payload));
              setIsAuthenticated(true);
              if (storedUser?.language) applyUserLanguage(storedUser.language);

              // Schedule refresh
              scheduleTokenRefresh(storedToken);

              console.log('[Auth] Session restored from storage');
            }
          }
        }
      } catch (error) {
        console.error('[Auth] Failed to restore session:', error);
        secureStorage.clearAll();
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, [refreshAccessToken, scheduleTokenRefresh]);

  /**
   * Listen for logout events from axios interceptor
   */
  useEffect(() => {
    const handleLogout = (event) => {
      console.log('[Auth] Logout event received:', event.detail);
      logout(true); // Skip API call since token is invalid
    };

    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [logout]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  /**
   * Get current access token (for manual API calls)
   */
  const getAccessToken = useCallback(() => {
    return accessToken || secureStorage.getAccessToken();
  }, [accessToken]);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      try { secureStorage.setUserData(next); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  const value = {
    user,
    updateUser,
    accessToken,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    refreshAccessToken,
    getAccessToken,
    // Legacy support
    token: accessToken,
    setToken: setAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
